// Encounter appointment-status control (PROD-1598 slice — the "status-change menu is
// deferred" TODO on patient-summary-rail, PROD-1329). The encounter header showed the
// appointment status as a READ-ONLY badge, so a clinician could never move the appointment
// (Arrived → CheckedIn → …) from the encounter — the status appeared stuck. Flutter's
// patient_info_widget renders an interactive status dropdown here; this restores parity.
//
// Wire path is the existing, proven one: useStatusList() (GET /api/Appointment/Status) for
// the wire status objects, useUpdateAppointmentStatus() (PUT /api/Appointment/Status) for the
// change — the same hooks the Calendar popup's AppointmentStatusManager uses. Because the
// encounter's status is sourced from tap-time nav (not the ['appointments'] cache the mutation
// patches), this component keeps its own `current` so the badge reflects the change immediately.
//
// Render is the DS SessionStatusControl (PROD-1588): the COLORED STATUS CHIP is the dropdown
// trigger — Flutter patient_info_widget / Figma parity.
//
// COLOUR NOW COMES FROM PM, via the `semantic` override (Andrew, 2026-08-28: "the appointment
// colors and naming please take them from pm react for the calendar, today screen, encounter
// patient card"). This header previously read "Colour derives from `value` via the DS canonical
// map — `semantic` is intentionally NOT passed"; that is no longer true and the line is replaced
// rather than left to contradict the code.
//
// The DS documents omitting `semantic` as "the only correct behaviour for appointment statuses";
// `semantic` is nonetheless a supported prop, so this is an API-sanctioned override, not a DS shadow.
// What actually changes: Cancelled goes grey (the canonical map painted it red) and
// Waitlist/Pending/Proposed go yellow. Arrived, Fulfilled and NoShow already agreed.
//
// Booked and CheckedIn do NOT change: PM paints them blue TINTS, which the chip can only do in its
// soft tone, and this component passes no `tone` — solid `info` resolves to #5A6B75, a gray. So
// Samer's PROD-1588 Booked = GRAY reject stands, unreversed, and needs no amendment. Details and the
// auditable block list live in `lib/pm-appointment-status.ts` (`PM_COLOUR_BLOCKED`).
//
// NAMING needs no change here: SESSION_STATUS_OPTIONS already reads "Arrived · Checked In ·
// In Session · No Show", and PM's labels for those are identical ("Checked In", "No Show").
//
// D1 SIX-STATUS ADOPTION (Samer decision strip 2026-07-22, PROD-1588): the menu is the DS
// SESSION_STATUS_OPTIONS default (the Figma six-list, node 7017:62, gated to its canonical
// survivors — Arrived · Checked In · In Session · No Show). This OVERRIDES the wave-2 R1
// Flutter 3-option lock; the previous local APPOINTMENT_STATUS_TRANSITIONS filter is removed.
// No `options` prop is passed — the DS default IS the option set (one source of truth).
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SessionStatusControl } from '@scripsteam/scrips-signal-ds';
import { pmRailStatusSemantic, pmStatusLabel } from '@/lib/pm-appointment-status';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import type { AppointmentStatus } from '@/types/scheduling-api';

/**
 * ⚠️ ANDREW'S REVIEW CAUTION — BINDING (PROD-1588 cure wave 7).
 * SessionStatusControl's default option set (SESSION_STATUS_OPTIONS) emits 'InSession',
 * which is NOT a Scrips.Core AppointmentStatus enum member (verified origin/master
 * 2026-07-22). The PA wiring MUST map the in-session family → 'CheckedIn' at the POST
 * boundary (PUT /api/Appointment/Status); NEVER post the raw emitted value — the backend
 * model-binder would reject (or worse, persist) an unknown code. 'In Session' is an
 * accepted DISPLAY alias only (touchpoint-3 ruling: the in-session family normalizes to
 * CheckedIn). Covered by encounter-status-control.test.tsx ("an 'InSession' selection
 * POSTs CheckedIn").
 */
export function toPostStatusCode(code: string): string {
  const normalized = code.replace(/[^a-z]/gi, '').toLowerCase();
  return normalized === 'insession' ? 'CheckedIn' : code;
}

export interface EncounterStatusControlProps {
  /** Appointment id — the encounter route param. PUT target for the status change. */
  appointmentId: string;
  /** Current status code from tap-time nav (e.g. "Arrived"). */
  status?: string;
  /** Fired after a successful change with the CANONICAL (posted) code so the shell can
   *  re-evaluate the Start gate — 'InSession' reports as 'CheckedIn' (the wire truth). */
  onChanged?: (code: string) => void;
}

export function EncounterStatusControl({ appointmentId, status, onChanged }: EncounterStatusControlProps) {
  const { data: statusList } = useStatusList();
  const update = useUpdateAppointmentStatus();
  const queryClient = useQueryClient();
  // What the pill shows is the appointment status FIELD (PROD-3045 N5).
  //
  // The prop is derived, never copied into state: on the reload/deep-link path the status arrives
  // from a query that resolves AFTER the first render, and a `useState(status)` copy left the
  // control rendering `null` for the life of the component (Andrew, 2026-08-24).
  //
  // A change the clinician just made bridges the gap until the field catches up, and no longer:
  // `selected` holds the CANONICAL code that was PUT (never the display alias), and it is dropped for
  // good the first time the field moves (the invalidation refetch, or the Scheduling hub reporting a
  // later Start or sign). Dropped for good, not merely hidden: if the field later returns to the value
  // it had when the pick was made (a correction elsewhere), the old pick must not reappear. The
  // field's last seen value is state compared during render (React's "adjust state when a prop
  // changes" pattern), not an effect. The pick also records the field value it was made OVER and
  // shows only while the field still holds it, so a PUT that resolves after the field has already
  // moved (a late response) never puts a stale pick back. Two defects this replaces: (1) the pill
  // kept the clinician's DISPLAY alias ("In Session") while the field, the started note's chip, Today
  // and the Calendar all said "Checked In", so one visit showed two statuses; (2) the override never
  // let go, so a sign made elsewhere left this pill on its old value until a remount.
  //
  // SWITCH ON ONLY for now (`?ef=frameV2`, read once per mount): Samer, 6 Oct, merge before the design
  // stamp only if every change is invisible with the switch OFF. OFF keeps today's control exactly
  // (the pick, alias included, shown until a remount; picks compared as emitted), and gets this
  // behaviour in the follow-up PR after the stamp.
  const [fieldWins] = useState(() => isEncounterFlagOn('frameV2'));
  const [selected, setSelected] = useState<{ code: string; over: string | undefined } | undefined>(undefined);
  const [seenStatus, setSeenStatus] = useState(status);
  if (fieldWins && status !== seenStatus) {
    setSeenStatus(status);
    if (selected !== undefined) setSelected(undefined);
  }
  const current = selected && (!fieldWins || selected.over === status) ? selected.code : status;

  const handleChange = (code: string) => {
    // ON: compared as the code that would be PUT, so "In Session" on a CheckedIn visit is no change.
    if (!code || (fieldWins ? toPostStatusCode(code) : code) === current || update.isPending) return;
    // POST-boundary mapping (see the BINDING caution above): the in-session family
    // posts the canonical CheckedIn code; every other option posts its own code.
    const postCode = toPostStatusCode(code);
    // The PUT body carries the server's own status object ({code, display, definition}) —
    // resolve it from the fetched status list; an unknown code is refused, never invented.
    const picked = statusList?.find((s) => (s.code ?? '').toLowerCase() === postCode.toLowerCase());
    if (!picked) return;
    update.mutate(
      { appointmentId, status: picked as AppointmentStatus },
      {
        onSuccess: () => {
          // ON: display AND host both get the canonical wire code (the appointment's real status),
          // so an "In Session" pick reads "Checked In", as the field will once it refetches; `status`
          // is the field value this request was made over (captured when it was sent). OFF, as today:
          // the display keeps the clinician's selection; the host still gets the canonical code.
          setSelected({ code: fieldWins ? picked.code : code, over: status });
          onChanged?.(picked.code);
          // Refresh the popup/panel badge + billing gate that key off details.
          queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
        },
      },
    );
  };

  // While the wire status list loads (or if it's empty), render the current status as a
  // readOnly pill — same chip, no chevron/menu — so the header never renders an empty
  // control AND a selection can never fire before the PUT body can be built.
  if (!statusList || statusList.length === 0) {
    return current ? (
      <SessionStatusControl
        value={current}
        semantic={pmRailStatusSemantic(current)}
        readOnly
        size="sm"
        aria-label={pmStatusLabel(current)}
      />
    ) : null;
  }

  return (
    <SessionStatusControl
      value={current ?? ''}
      // PM's palette. `current` may be the display alias 'InSession' (switch OFF keeps the clinician's
      // pick), which PM has no bucket for — `pmRailStatusSemantic` folds the in-session family onto
      // CheckedIn first, so it paints like PM's checkedIn rather than falling through to PM's `booked`
      // default.
      semantic={pmRailStatusSemantic(current)}
      // No `options`: the DS SESSION_STATUS_OPTIONS default is the D1 six-list (gated).
      onChange={handleChange}
      disabled={update.isPending}
      size="sm"
      aria-label="Update status"
    />
  );
}
